<template>
  <Teleport to="body">
    <Transition name="modal">
      <!-- Aucune fermeture au clic sur l'overlay : un clic hors modale (ou un
           relâchement de souris après une sélection de texte pour copier/coller)
           fermait la fenêtre par accident et faisait perdre la saisie.
           La croix du header est le seul moyen de fermer. -->
      <div
        v-if="modelValue"
        class="modal-overlay"
        role="presentation"
      >
        <div
          ref="modalRef"
          :class="['modal', `modal-${size}`, panelClass]"
          role="dialog"
          :aria-modal="true"
          :aria-labelledby="titleId"
          :aria-describedby="descriptionId"
          tabindex="-1"
          :data-testid="$attrs['data-testid']"
        >
          <div class="modal-header" v-if="$slots.header || title">
            <slot name="header" :title-id="titleId">
              <h2 v-if="title" :id="titleId">{{ title }}</h2>
            </slot>
            <Button 
              v-if="closable" 
              variant="close" 
              icon="fas fa-times"
              aria-label="Fermer la fenêtre"
              @click="handleClose"
            >
              Fermer
            </Button>
          </div>
          
          <div class="modal-body" :class="{ 'modal-body-flush': flush }" :id="descriptionId">
            <slot></slot>
          </div>
          
          <div class="modal-footer" v-if="$slots.footer">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { Button } from './index'
import { useFocusTrap } from '../../composables/ui/useFocusTrap.js'

// Racine Teleport (fragment) : l'héritage automatique des attrs est impossible
// et Vue warn à chaque data-testid posé par un parent. Les attrs utiles sont
// déjà rebindés explicitement sur le div .modal (data-testid).
defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: null
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large', 'xlarge', 'fullscreen'].includes(value)
  },
  closable: {
    type: Boolean,
    default: true
  },
  description: {
    type: String,
    default: null
  },
  /**
   * Retire le rembourrage et le défilement du corps : la modale qui l'active
   * fournit sa propre mise en page interne (colonnes, zone défilante à elle).
   */
  flush: {
    type: Boolean,
    default: false
  },
  /**
   * Classe posée sur le panneau. Le panneau étant téléporté vers <body>, il
   * sort de l'arbre de l'appelant : ni `class` (inheritAttrs désactivé) ni
   * `:deep()` ne l'atteignent. Cette prop est le point d'ancrage des habillages
   * propres à une modale (taille du titre, pied teinté…).
   */
  panelClass: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'close'])

const modalRef = ref(null)
const isActive = computed(() => props.modelValue)

useFocusTrap(modalRef, isActive)

const titleId = computed(() => props.title ? `modal-title-${Math.random().toString(36).substr(2, 9)}` : null)
const descriptionId = computed(() => props.description ? `modal-description-${Math.random().toString(36).substr(2, 9)}` : null)

const handleClose = () => {
  emit('update:modelValue', false)
  emit('close')
}

const handleKeyDown = (e) => {
  if (e.key === 'Escape' && props.modelValue) {
    handleClose()
  }
}

watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
  } else {
    document.removeEventListener('keydown', handleKeyDown)
    document.body.style.overflow = ''
  }
})
</script>

<style scoped>
/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: var(--spacing-md);
  overflow-y: auto;
}

.modal {
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  width: 90%;
  max-width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  border: 1px solid var(--color-border);
  margin: auto;
}

.modal-small {
  max-width: 400px;
}

.modal-medium {
  max-width: 600px;
}

.modal-large {
  max-width: 800px;
}

.modal-xlarge {
  max-width: 1200px;
}

/* Quasi plein écran : le popup prend un max de place (ex. tunnel « Analyse du besoin »). */
.modal-fullscreen {
  width: 95vw;
  max-width: 95vw;
  height: 90vh;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-xl) var(--spacing-lg);
  border-bottom: 1px solid var(--color-border);
  position: sticky;
  top: 0;
  background: var(--color-bg-primary);
  z-index: 10;
}

.modal-header h2,
.modal-header h3 {
  margin: 0;
  color: var(--color-text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.modal-header h3 {
  font-size: 1.25rem;
}

.modal-body {
  padding: var(--spacing-xl);
  overflow-y: auto;
  flex: 1;
}

/* Le défilement passe à la charge du contenu : sans ce overflow caché, deux
   ascenseurs se superposent (celui du corps et celui de la colonne interne).
   `display: flex` + `min-height: 0` sont ce qui donne au contenu une hauteur
   FERME : sans eux, un `height: 100%` à l'intérieur se résout contre une boîte
   sans hauteur déclarée, retombe sur `auto`, et plus rien ne défile. */
.modal-body-flush {
  padding: 0;
  overflow: hidden;
  display: flex;
  min-height: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  border-top: 1px solid var(--color-border);
  background: var(--color-bg-primary);
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 0;
    align-items: flex-end;
  }
  
  .modal {
    width: 100%;
    max-width: 100%;
    max-height: 95vh;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    margin: 0;
  }
  
  .modal-header {
    padding: var(--spacing-md) var(--spacing-lg);
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  .modal-header h2,
  .modal-header h3 {
    font-size: 1.25rem;
  }
  
  .modal-body {
    padding: var(--spacing-lg);
    max-height: calc(95vh - 120px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .modal-body-flush {
    padding: 0;
    max-height: none;
    overflow: hidden;
  }
  
  .modal-footer {
    flex-direction: column-reverse;
    padding: var(--spacing-md);
    position: sticky;
    bottom: 0;
    background: var(--color-bg-primary);
    border-top: 1px solid var(--color-border-light);
  }
  
  .modal-footer :deep(.btn) {
    width: 100%;
    justify-content: center;
    min-height: 44px;
  }
  
  /* Swipe to close indicator */
  .modal::before {
    content: '';
    position: absolute;
    top: var(--spacing-2);
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 4px;
    background: var(--color-border);
    border-radius: 2px;
    z-index: 11;
  }
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}

.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease;
}

.modal-enter-from {
  opacity: 0;
}

.modal-enter-from .modal {
  transform: translateY(30px) scale(0.95);
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-leave-to .modal {
  transform: translateY(20px) scale(0.98);
  opacity: 0;
}
</style>

